// Canonical code-export framework set (frames only; DS code export removed). Known
// frameworks get server-owned scaffolding instructions; clients can also send any string
// (e.g. "SwiftUI") paired with `customInstructions` to describe the scaffold. No
// sentinel — unknown string = custom. Exported as a runtime `as const` array so callers
// can iterate/include-check at runtime without redeclaring. The `(string & {})` keeps
// literal autocomplete while allowing any string on the wire.
export const EXPORT_CODE_FRAMEWORKS = [
	'React + Tailwind',
	'Next.js + Tailwind',
	'Plain HTML/CSS',
] as const;

export type ExportCodeFramework = (typeof EXPORT_CODE_FRAMEWORKS)[number];
export type ExportCodeFrameworkOrCustom = ExportCodeFramework | (string & {});

export interface ExportFramesCodeRequest {
	frameIds: string[];
	framework: ExportCodeFrameworkOrCustom;
	customInstructions?: string;
	// Desired download filename. Plumbed into the GCS signed URL via
	// response-content-disposition so cross-origin downloads keep the pretty name.
	filename?: string;
}

export interface ExportFramesCodeResponse {
	exportId: string;
}

export interface ExportFramesAssetsRequest {
	frameIds: string[];
	// Raw per-frame formats (png/html) and the grouped formats (html+images/images).
	// For raw formats with a single frame the server emits the file directly; with
	// multiple frames it bundles into a zip. Grouped formats always zip. SVG exports
	// capture client-side (studio's devigner postMessage path sees live edit state)
	// but stage + package server-side — see ExportStageFrameSvgRequest.
	format: 'png' | 'html' | 'html+images' | 'images';
	// Desired download filename. See note on ExportFramesCodeRequest.filename.
	filename?: string;
}

export interface ExportFramesAssetsResponse {
	exportId: string;
}

// Stage one frame's captured SVG for server-side packaging. The client sends the
// pre-embed SVG (image references are still URLs, so the payload is small); the
// server embeds images/icons, stores the inflated result, and returns an opaque
// handle. The inflated bytes never cross HTTP in either direction.
export interface ExportStageFrameSvgRequest {
	svg: string;
	width: number;
	height: number;
}

export interface ExportStageFrameSvgResponse {
	assetId: string;
}

// Create the pending ExportLink before capture/staging begins, so the Downloads
// modal and the in-flight indicators show the export for its whole duration —
// the same lifecycle job-backed exports get from their prepare step.
export interface ExportPrepareSvgAssetsRequest {
	// Desired download filename. See note on ExportFramesCodeRequest.filename.
	filename: string;
}

export interface ExportPrepareSvgAssetsResponse {
	exportId: string;
}

// Package previously staged SVG assets into a download: one asset → a plain .svg,
// multiple → a zip with a folder per asset. Flips the prepared ExportLink to ready.
export interface ExportPackageSvgAssetsRequest {
	exportId: string;
	assets: Array<{ assetId: string; folderName: string }>;
}

export interface ExportPackageSvgAssetsResponse {
	exportId: string;
}

// Flip a prepared-but-unfinished SVG export to failed. The client drives the
// capture/staging phase, so on error it is the only actor that can stop the
// pending row's spinner. No-ops if the export already reached a terminal state.
export interface ExportFailSvgAssetsRequest {
	exportId: string;
	// Technical cause, for the request log.
	error?: string;
	// True when the browser couldn't capture a screen; any other failure step is
	// reported to the user generically.
	captureFailed?: boolean;
}

export interface ExportFailSvgAssetsResponse {
	exportId: string;
}

// Deprecated upload-then-mint-ExportLink endpoint, superseded by the stage/package
// pair above. Kept for backward compatibility with older studio clients; remove
// together with /export/upload-asset.
export interface ExportPrebuiltAssetRequest {
	// Base64-encoded bytes of the asset to store.
	data: string;
	// Desired download filename. Served via the same signed-URL path as other exports.
	filename: string;
	// File extension for the stored object (e.g. 'svg' or 'zip').
	extension: string;
	// Human-readable label shown in the downloads center.
	label: string;
}

export interface ExportPrebuiltAssetResponse {
	exportId: string;
}

// Max frames per LLM-backed export request (code and AI). Enforced on both client and server.
export const MAX_LLM_EXPORT_FRAMES = 10;

// Max frames per SVG export. Enforced on the client before capture/staging begins
// (an over-limit export would otherwise stage every asset only to fail at package
// time) and on the server, where it also bounds memory during zip assembly.
export const MAX_SVG_EXPORT_FRAMES = 200;

// Everything a user can be told about an export that didn't succeed: the Downloads row,
// the failure toast, and the label for an export that never finished. Forge writes these
// onto failed exports; studio shows them as-is. Each says what happened in the user's
// terms and what to do next, never internal details.
export const EXPORT_MESSAGES = {
	failed: 'Export failed. Please try again.',
	interrupted: "This export didn't finish. Please try again.",
	tooLargeScreens: 'Export size limit reached. Select fewer screens and try again.',
	tooLargeScreen:
		'Export size limit reached. Try simplifying this screen, or export it as HTML instead.',
	tooLargeDsRequest: 'Export size limit reached. Ask for less, and try again.',
	screenNotGenerated:
		'One of the selected screens has nothing to export yet. All screens in the selection must be fully generated to export.',
	screensMissing: 'One or more of the selected screens no longer exist.',
	designSystemMissing: 'This design system no longer exists.',
	designSystemEmpty: "This design system is empty, so there's nothing to export yet.",
	svgCaptureFailed: "We couldn't capture this screen as an SVG. Reload the page and try again.",
} as const;

export type ExportFramesAiPreset = 'spec' | 'style' | 'custom';

export interface ExportFramesAiRequest {
	frameIds: string[];
	preset: ExportFramesAiPreset;
	customBrief?: string;
	// Desired download filename. See note on ExportFramesCodeRequest.filename.
	filename?: string;
}

export interface ExportFramesAiResponse {
	exportId: string;
}

// DS export presets. `all` = complete DS pack; `components` / `tokens` are filtered
// slices; `custom` runs an LLM agent that pulls DS content via tools based on the user's
// free-form prompt.
export type ExportDsAiPreset = 'all' | 'components' | 'tokens' | 'custom';

export interface ExportDsAiRequest {
	designSystemId: string;
	preset: ExportDsAiPreset;
	componentNames?: string[];
	tokenDomains?: string[];
	customPrompt?: string;
	// Desired download filename. See note on ExportFramesCodeRequest.filename.
	filename?: string;
}

export interface ExportDsAiResponse {
	exportId: string;
}
